'use client';

import dynamic from 'next/dynamic';
import { ComponentProps, FC, PropsWithChildren, ReactNode, useMemo, useState } from 'react';
import { shallow } from 'zustand/shallow';

import { productDetailPayloadToBaseCarData } from 'src/data/ProductApi/ProductDetailPayloadToBaseCarData';
import { CarPriceCard } from 'src/pdp/components/CarPriceCard/CarPriceCard';
import { CarStickyBarContent } from 'src/pdp/components/CarStickyBar/CarStickyBarContent';
import { PdpLayout } from 'src/pdp/layouts/PdpLayout/PdpLayout';
import { useUpdatedCarStatus } from 'src/plp/components/Results/useUpdatedCarStatus';
import { authStore } from 'src/stores/authStore';
import { cartStore } from 'src/stores/cartStore';
import { pdpStore } from 'src/stores/pdpStore';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';
import { CarDisplayState, formatCarDisplayState } from './pdpHelpers';

const CarStickyBar = dynamic(
  () => import('src/pdp/components/CarStickyBar/CarStickyBar').then((mod) => mod.CarStickyBar),
  { ssr: false },
);

type GalleryDisplay = 'view360' | 'gallery' | 'imperfections';
export type PdpModalOpen = 'governmentCharges' | 'purchaseInProgress' | 'carSwitch' | 'enquiry' | null;

export interface GalleryModalState {
  displayState: GalleryDisplay;
  openDoors: boolean;
  exterior: boolean;
  galleryStartIndex?: number;
}

interface Props {
  productDetails: ProductDetailsPayload;
  hero: ReactNode;
  coreValues: ReactNode;
  gallery: ReactNode;
  microcopy: {
    priceCard: ComponentProps<typeof CarPriceCard>['microcopy'];
    stickyBar: ComponentProps<typeof CarStickyBarContent>['microcopy'];
  };
}

export const Pdp: FC<PropsWithChildren<Props>> = ({
  productDetails,
  hero,
  coreValues,
  gallery,
  microcopy,
  children,
}) => {
  const { isUserLoggedIn, userOrdersLoading, userOrders } = authStore(
    (state) => ({
      isUserLoggedIn: state.isUserLoggedIn,
      userOrdersLoading: state.userOrdersLoading,
      userOrders: state.userOrders,
    }),
    shallow,
  );
  const { carLoaded, carData, cartExpired, apiCartData, apiCartLoaded } = cartStore(
    (state) => ({
      carLoaded: state.carLoaded,
      carData: state.carData,
      cartExpired: state.cartExpired,
      apiCartData: state.apiCartData,
      apiCartLoaded: state.apiCartLoaded,
    }),
    shallow,
  );
  const { setCurrentModal } = pdpStore(
    ({ setCurrentModal }) => ({
      setCurrentModal,
    }),
    shallow,
  );
  const [carDisplayState, setCarDisplayState] = useState<CarDisplayState>('loading');
  const carStatus = useUpdatedCarStatus(productDetails.sku, productDetails.state);

  const isCheckoutProgress = useMemo(() => carLoaded && !!carData && !cartExpired, [carLoaded, carData, cartExpired]);

  useMemo(() => {
    const carDisplayState = formatCarDisplayState(
      productDetails.sku,
      apiCartData,
      userOrders,
      isUserLoggedIn,
      apiCartLoaded && !userOrdersLoading,
      carStatus,
    );
    setCarDisplayState(carDisplayState);
    if (carDisplayState === 'not-available' && (carStatus === 'draftOrder' || carStatus === 'reserved')) {
      setCurrentModal({ name: 'purchaseInProgress' });
      pushToDataLayer({
        event: 'pdp_interaction',
        type: 'view',
        value: 'car_reserved',
      });
    }
  }, [apiCartData, isUserLoggedIn, userOrdersLoading, userOrders, apiCartLoaded, carStatus, carDisplayState]);

  return (
    <>
      <PdpLayout
        hero={hero}
        price={
          <>
            <CarPriceCard
              productDetails={productDetails}
              displayState={carDisplayState}
              isCheckoutProgress={isCheckoutProgress}
              microcopy={microcopy.priceCard}
            />
            {coreValues}
          </>
        }
        gallery={gallery}
      >
        {children}
      </PdpLayout>

      <CarStickyBar>
        <CarStickyBarContent
          isCheckoutProgress={isCheckoutProgress}
          carData={productDetailPayloadToBaseCarData(productDetails)}
          displayState={carDisplayState}
          microcopy={microcopy.stickyBar}
        />
      </CarStickyBar>
    </>
  );
};
